import type { PublicKeyCredentialRequestOptionsJSON } from "@simplewebauthn/browser";

export type PasskeyPlatform = "apple" | "windows" | "other";
export type PasskeyAuthenticator = "any" | "hybrid" | "local";

export function detectPasskeyPlatform(userAgent: string): PasskeyPlatform {
  if (/Windows/iu.test(userAgent)) return "windows";
  if (/Macintosh|Mac OS X|iPhone|iPad/iu.test(userAgent)) return "apple";
  return "other";
}

export function passkeySignInLabel(platform: PasskeyPlatform): string {
  if (platform === "windows") return "Sign in with your Yodl password first";
  return "Sign in with Touch ID or passkey";
}

export function passkeyVerificationLabel(platform: PasskeyPlatform): string {
  if (platform === "windows") return "Use iPhone — show QR code";
  return "Continue with Touch ID or passkey";
}

export function authenticationOptionsForAuthenticator(
  options: PublicKeyCredentialRequestOptionsJSON,
  authenticator: PasskeyAuthenticator
): PublicKeyCredentialRequestOptionsJSON {
  if (authenticator === "any") return options;
  const transport = authenticator === "hybrid" ? "hybrid" as const : "internal" as const;
  const hint = authenticator === "hybrid" ? "hybrid" as const : "client-device" as const;
  const allowCredentials = options.allowCredentials?.map((credential) => ({ ...credential, transports: [transport] }));
  return {
    ...options,
    ...(allowCredentials === undefined ? {} : { allowCredentials }),
    hints: [hint]
  };
}

export function passkeyEnrollmentLabel(platform: PasskeyPlatform): string {
  if (platform === "windows") return "Create passkey with Windows Hello PIN";
  return platform === "apple" ? "Save passkey in Apple Passwords" : "Create passkey on this device";
}

export function passkeyEnrollmentNickname(platform: PasskeyPlatform): string {
  if (platform === "windows") return "Windows Hello";
  if (platform === "apple") return "Apple Passwords";
  return "Device passkey";
}

export function passkeyEnrollmentNicknameForAuthenticator(authenticator: PasskeyAuthenticator, platform: PasskeyPlatform): string {
  if (authenticator === "local") return passkeyEnrollmentNickname(platform);
  if (authenticator === "hybrid") return "Phone or tablet passkey";
  return "Browser passkey manager";
}

export function passkeySettingsNickname(platform: PasskeyPlatform, userAgent: string): string {
  if (platform === "windows") return "Windows Hello";
  if (/iPhone|iPad/iu.test(userAgent)) return "iPhone or iPad";
  if (platform === "apple") return "Mac Touch ID";
  return "This device";
}
